import { useContext, useEffect, useState } from "react";
import {
  TkRStaffChangesTableProps,
  TkRStaffChangeProps,
} from "../../../type/componentsType/ITkRStaffChangesTableProps";
import TkTable from "../../../components/atoms/TkTable";
import TkRaio from "../../../components/atoms/TkRadio";
import "./_style.scss";
import TkButton from "../../../components/atoms/TkButton";
import { Box } from "@mui/material";
import TkBackButton from "../../../components/molecules/TkBackButton";
import { httpIAPI_FAC010 } from "../api/request";
import { GlobalContext } from "../../../context/globalContext";
import { BusinessContext } from "../../../context/businessContext";
import { useLocation, useNavigate } from "react-router-dom";
import TkMessageDialog from "../../../components/molecules/TkMessageDialog";
import authorityUserType from "../../../assets/data/TkAuthorityProperty";

/**
 * スタッフ変更を表示するためのテーブル部品をレンダリングします。
 * @param {staffs} props スタッフ変更情報。
 * @param {onChange} props スタッフ変更情報が変更された際のコールバック関数。
 * @returns TkRadioStaffChangesTable スタッフ変更テーブル部品
 */
const TkRadioStaffChangesTable: React.FC<TkRStaffChangesTableProps> = (
  props
) => {
  const { staffs, disabled, allDisabled, onChange } = props;

  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const location = useLocation();
  const locationState = location.state;
  const [staffList, setStaffList] = useState<TkRStaffChangeProps[]>(staffs);
  const [bodyList, setBodyList] = useState<{ children: any }[][]>([]);
  const [open, setOpen] = useState(false);
  const [upData, setUpData] = useState<
    { staffNo: string; transferReason: string; updateDateTime: string }[]
  >([]);
  const [selections, setSelections] = useState(Array(staffs.length).fill("0"));

  const userType = state.authInfo?.userType;

  // テーブルタイトル
  const headList = [
    {
      headTitle: "配置換え",
      helpText: (
        <>
          施設の廃止、休止、経営移管にあたり、法人内の他の施設・事業に配置換えする場合に選択してください。
          <br />
          ※契約者が経営する契約対象外施設への異動の場合は、復帰の予定がなければ「退職」としてください。
        </>
      ),
      className: "tkStaffChangesHead",
    },
    {
      headTitle: "譲渡先法人へ異動",
      helpText: (
        <>
          吸収合併、経営移管により施設・事業を譲渡する場合、譲渡先法人（※当共済制度の契約者である必要があります。
          <br />
          経営引継先の法人のことです）でそのまま雇用される職員が該当します。
          <br />
          譲渡先法人が当共済制度の契約者でない場合は、退職を選択してください。
        </>
      ),
      className: "tkStaffChangesHead",
    },
    {
      headTitle: "退職（廃止日が退職日となります。）",
      helpText: (
        <>
          当制度では、施設廃止・施設譲渡などにより被共済職員でなくなる場合は退職となります。
          <br />
          詳しくは、
          <a
            href={`${
              process.env.PUBLIC_URL ?? ""
            }/assets/pdf/≪別紙≫本制度の「退職」について.pdf`}
            target="_blank"
            rel="noreferrer"
          >
            こちら
          </a>
          をご確認ください。
        </>
      ),
      className: "tkStaffChangesHead",
    },
    {
      headTitle: "氏名",
    },
    {
      headTitle: "性別",
    },
    {
      headTitle: "生年月日",
    },
    {
      headTitle: "職種",
    },
  ];

  const handleCountSelections = async () => {
    try {
      const res = await httpIAPI_FAC010({
        contractorNo:
          userType === authorityUserType.userTypeNumber.KIKO
            ? businessState.common?.contractorNo!
            : state.authInfo?.contractorNo!,
        facilityNo: locationState?.facilityNo!,
        applyId: businessState.job_18?.applyId!,
        staffList: upData,
        businessType: businessState.common?.businessType,
        transferDate: locationState?.transferDate!,
        screenId: "SCRC050123",
      });
      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        setBusinessState({
          ...businessState,
          job_18: { ...businessState.job_18, applyId: res.applyId },
        });
        navigate("/SCRC050121");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleUpRadioChange = (rowIndex: number, option: any) => {
    const newSelections = [...selections];
    newSelections[rowIndex] = option;
    setSelections(newSelections);
  };

  const handleRadioChange = (
    index: number,
    type: "reconfiguration" | "transferee" | "resignation"
  ) => {
    const newStaffs = staffList.map((staff, i) => {
      if (i !== index) {
        return staff; // 他のスタッフは変更しない
      } else {
        const updatedStaff = { ...staff };
        updatedStaff.reconfiguration = type === "reconfiguration" ? "1" : "0";
        updatedStaff.transferee = type === "transferee" ? "1" : "0";
        updatedStaff.resignation = type === "resignation" ? "1" : "0";
        return updatedStaff;
      }
    });

    setStaffList(newStaffs);
    onChange && onChange(newStaffs);
  };

  useEffect(() => {
    const dataList: {
      staffNo: string;
      transferReason: string;
      updateDateTime: string;
    }[] = [];
    const list = staffList.map((staff) => {
      const data = {
        staffNo: staff.staffNo,
        transferReason:
          staff.reconfiguration === "1"
            ? "99"
            : staff.transferee === "1"
            ? "12"
            : staff.resignation === "1"
            ? "2"
            : "",
        updateDateTime: staff.updateDateTime,
      };
      dataList.push(data);
      return [
        {
          children: (
            <TkRaio
              data-testid={`reconfiguration${staff.index}`}
              checked={staff.reconfiguration === "1"}
              name={`${staff.index}`}
              onChange={(e: any) =>
                handleUpRadioChange(staff.index, e.target.value)
              }
              onClick={() => handleRadioChange(staff.index, "reconfiguration")}
              value="reconfiguration"
              disabled={allDisabled}
            />
          ),
          align: "center",
          width: "10%",
          className: "tkStaffChangesBody",
        },
        {
          children: (
            <TkRaio
              data-testid={`transferee${staff.index}`}
              checked={staff.transferee === "1"}
              name={`${staff.index}`}
              onChange={(e: any) =>
                handleUpRadioChange(staff.index, e.target.value)
              }
              onClick={() => handleRadioChange(staff.index, "transferee")}
              value="transferee"
              disabled={disabled || allDisabled}
            />
          ),
          align: "center",
          width: "10%",
          className: "tkStaffChangesBody",
        },
        {
          children: (
            <TkRaio
              data-testid={`resignation${staff.index}`}
              checked={staff.resignation === "1"}
              name={`${staff.index}`}
              onChange={(e: any) =>
                handleUpRadioChange(staff.index, e.target.value)
              }
              onClick={() => handleRadioChange(staff.index, "resignation")}
              value="resignation"
              disabled={allDisabled}
            />
          ),
          align: "center",
          width: "10%",
          className: "tkStaffChangesBody",
        },
        {
          children: (staff.staffNo ?? "") + ":" + (staff.staffNameKanji ?? ""),
          width: "30%",
          className: "tkStaffChangesBody",
          backgroundColor:
            staff.reconfiguration === "0" &&
            staff.transferee === "0" &&
            staff.resignation === "0"
              ? "#ffff00"
              : "",
        },
        {
          children: staff.sex,
          width: "10%",
          className: "textDisplay",
          align: "center",
          backgroundColor:
            staff.reconfiguration === "0" &&
            staff.transferee === "0" &&
            staff.resignation === "0"
              ? "#ffff00"
              : "",
        },
        {
          children: staff.birthday,
          width: "10%",
          className: "textDisplay",
          align: "center",
          backgroundColor:
            staff.reconfiguration === "0" &&
            staff.transferee === "0" &&
            staff.resignation === "0"
              ? "#ffff00"
              : "",
        },
        {
          children: staff.jobName,
          width: "10%",
          className: "textDisplay",
          align: "center",
          backgroundColor:
            staff.reconfiguration === "0" &&
            staff.transferee === "0" &&
            staff.resignation === "0"
              ? "#ffff00"
              : "",
        },
      ];
    });
    setBodyList([...list]);
    setUpData(dataList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList]);

  return (
    <Box>
      <TkTable headList={headList} bodyList={bodyList} />

      <Box className="buttonBox">
        <TkBackButton />
        <TkButton onClick={() => setOpen(true)} disabled={!staffs.length || allDisabled}>
          登録
        </TkButton>
      </Box>

      <TkMessageDialog
        open={open}
        onOky={() => {
          handleCountSelections();
          setOpen(false);
        }}
        title="確認（情報を登録します）"
        contextText="　入力した内容で登録を行いますが、問題ないでしょうか。"
        okText="はい"
        cancelText="いいえ"
        onCancel={() => setOpen(false)}
      />
    </Box>
  );
};

export default TkRadioStaffChangesTable;
